import { Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/core/alerts/alert.service';
import { EMPTY, throwError, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as CasesActions from './cases.actions';
import { CasesSelectors } from './services';
import { CasesService } from './services/cases.service';
import { environment } from '../../../../../environments/environment';
import { LegacyService } from '../../../../legacy/legacy.service';
import { SessionSelectors } from '@store';
import { RouterSelectors } from '../../../../store/services/router.selectors';

@Injectable()
export class CasesEffects {
    LoadCases$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.loadCases),
        withLatestFrom(this.sessionSelector.recentClientKid$),
        switchMap(([, kid]) => {
                if (!kid) { return EMPTY; }
                return this.casesService.getCases(kid).pipe(
                    map(cases => CasesActions.loadCasesSuccess({ cases })),
                    catchError(() => EMPTY)
                );
            }
        )
    ));

    LoadRegnskabsaar$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.loadRegnskabsaar),
        withLatestFrom(this.sessionSelector.recentClientKid$),
        switchMap(([, kid]) => {
                if (!kid) { return EMPTY; }
                return this.casesService.getRegnskabsaar(kid).pipe(
                    map(v2RegnskabsaarKunde => CasesActions.loadRegnskabsaarSuccess({ v2RegnskabsaarKunde })),
                    catchError(() => EMPTY)
                );
            }
        )
    ));

    createRegnskabsaar$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.createRegnskabsaar),
        concatLatestFrom(() => this.routerSelector.routeNestedParams$),
        switchMap(([action, params]) => {
            return this.casesService.createRegnskabsaar(params.kid, action.start, action.slut).pipe(
                map(v2RegnskabsaarKunde => CasesActions.createRegnskabsaarSuccess({ v2RegnskabsaarKunde })),
                catchError((err) => throwError(err))
            );
        })
    ));

    updateRegnskabsaarSlut$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.updateRegnskabsaarSlut),
        switchMap(action => {
            return this.casesService.updateRegnskabsaarSlut(action.id, action.slut).pipe(
                map(v2RegnskabsaarKunde => CasesActions.updateRegnskabsaarSuccess({ v2RegnskabsaarKunde })),
                catchError((err) => throwError(err))
            );
        })
    ));

    updateRegnskabsaarVederlag$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.updateRegnskabsaarVederlag),
        switchMap(action => {
            return this.casesService.updateRegnskabsaarVederlag(action.id, action.vederlag).pipe(
                map(v2RegnskabsaarKunde => CasesActions.updateRegnskabsaarSuccess({ v2RegnskabsaarKunde })),
                catchError((err) => throwError(err))
            );
        })
    ));

    updateClientCase$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.updateClientCase),
        switchMap(action => {
            const partialSag = action.partialSagView;
            return this.casesService.updateClientCase(action.sagId, partialSag).pipe(
                map(sag => CasesActions.updateClientCaseSuccess({ sag })),
                catchError((err) => {
                        if (err.status === 409) {
                            const sag = err.error;
                            const url = `${environment.legacyUrl}/Timesag/laassag.asp?KID=${sag.kid}&GRP=${sag.yid}`;
                            const params = 'scrollbars, resizable, width=500,height=400,top=300, left=300';
                            this.legacyService.navigate(url, 'Laassag', params);
                            return EMPTY;
                        }
                        return throwError(err);
                    }
                )
            );
        })
    ));

    updateClientCaseV2$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.updateClientCaseV2),
        concatLatestFrom(() => this.routerSelector.routeNestedParams$),
        switchMap(([action, params]) => {
            const regnskabsaarId = params.regnskabsaarId;
            return this.casesService.updateClientCaseV2(regnskabsaarId, action.pid, action.partialSag).pipe(
                map(sag => {
                    if (sag.alert) {
                        const alertLines = sag.alert.split('\n');

                        alertLines.forEach((line) => {
                            this.alert.createAlert(line, 'warning');
                        });
                    }
                    return CasesActions.updateClientCaseSuccess({ sag });
                }),
                catchError((err) => {
                        return throwError(err);
                    }
                )
            );
        })
    ));

    createClientCase$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.createClientCase),
        switchMap(action => {
            const partialSag = action.partialSag;
            return this.casesService.createClientCase(partialSag).pipe(
                map(sag => CasesActions.createClientCaseSuccess({ sag })),
                catchError((err) => throwError(err))
            );
        })
    ));

    createClientCaseV2$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.createClientCaseV2),
        switchMap(action => {
            const partialSag = action.partialSag;
            return this.casesService.createClientCaseV2(partialSag).pipe(
                map(sag => CasesActions.createClientCaseSuccess({ sag })),
                catchError((err) => throwError(err))
            );
        })
    ));

    deleteClientCase$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.deleteClientCase),
        switchMap((action) => this.casesService.deleteClientCase(action.pid).pipe(
            map(() => CasesActions.deleteClientCaseSuccess({ pid: action.pid })),
            catchError((err) => throwError(err))
        ))
    ));

    copyClientCase$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.copyClientCase),
        switchMap(action => {
            const pid = action.pid;
            const caseCopyOptions = action.caseCopyOptions;
            return this.casesService.copyClientCase(pid, caseCopyOptions).pipe(
                map(sager => CasesActions.copyClientCaseSuccess({ sager })),
                catchError((err) => throwError(err))
            );
        })
    ));

    selectCase$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.selectCase),
        map((payload) => {
            if (payload.sagId) {
                return CasesActions.loadTimeBudgets({sagId: payload.sagId});
            }
            return CasesActions.selectCaseNone();
        })
    ));

    loadTimeBudgets$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.loadTimeBudgets),
        switchMap((payload) => {
            return this.casesService.loadTimeBudgets(payload.sagId).pipe(
                map(timeBudgets => CasesActions.loadTimeBudgetsSuccess({timeBudgets})),
                catchError(() => EMPTY)
            );
        })
    ));

    loadTimeBudgetsSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.loadTimeBudgetsSuccess),
        map((payload) => {
            const userIds = payload.timeBudgets.map(timeBudget => timeBudget.medID);
            const caseUsers = userIds.filter((v, i, a) => a.indexOf(v) === i);
            return CasesActions.initCaseUsers({caseUserIds: caseUsers});
        })
    ));

    updateTimeBudget$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.updateTimeBudget),
        switchMap((payload) => {
            return this.casesService.updateTimeBudget(payload.timeBudget).pipe(
                map(timeBudget => CasesActions.updateTimeBudgetSuccess({timeBudget})),
                catchError(() => EMPTY)
            );
        })
    ));

    createTimeBudget$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.createTimeBudget),
        mergeMap((payload) => {
            return this.casesService.createTimeBudget(payload.timeBudget).pipe(
                map(timeBudget => CasesActions.createTimeBudgetSuccess({timeBudget})),
                catchError(() => EMPTY)
            );
        })
    ));

    deleteTimeBudget$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.deleteTimeBudget),
        switchMap((payload) => {
            const oid = payload.oid;
            return this.casesService.deleteTimeBudget(oid).pipe(
                map(() => CasesActions.deleteTimeBudgetSuccess({ oid })),
                catchError(() => EMPTY)
            );
        })
    ));

    removeCaseUser$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.removeCaseUser),
        withLatestFrom(this.casesSelector.timeBudgets$),
        switchMap(([payload, timeBudgets]) => {
            const userToRemove = payload.userToRemove;
            const userTimeBudgets = timeBudgets.filter(t => t.medID === userToRemove);
            const userHours = userTimeBudgets.reduce((p, c) => p + c.timer, 0);
            if (userTimeBudgets.length > 0 && userHours === 0) {
                const timeBudgetsZeroIds = userTimeBudgets.filter(t => t.timer === 0).map(tb => tb.oid);
                return this.casesService.deleteTimeBudgets(timeBudgetsZeroIds).pipe(
                    map(() => CasesActions.removeCaseUserSuccess({userToRemove})),
                    catchError(() => EMPTY)
                );
            }
            if (userTimeBudgets.length > 0 && userHours > 0) {
                return of(CasesActions.removeCaseUserFailed());
            }
            return of(CasesActions.removeCaseUserSuccess({userToRemove}));
        })
    ));

    removeCaseUserFailed$ = createEffect(() => this.actions$.pipe(
        ofType(CasesActions.removeCaseUserFailed),
        tap(() => this.alert.createAlert('Medarbejdere med planlagte timer kan ikke fjernes!', 'danger'))
    ), {dispatch: false});

    constructor(
        private actions$: Actions,
        private casesService: CasesService,
        private casesSelector: CasesSelectors,
        private sessionSelector: SessionSelectors,
        private routerSelector: RouterSelectors,
        private alert: AlertService,
        private legacyService: LegacyService
    ) {}
}
